import { useEffect, useState, useCallback } from "react"
import { useNavigate, useParams, Link } from "react-router-dom";
import useErroPadrao from '../../../hooks/useErroPadrao';
import useHelp from '../../../hooks/useHelp';
import AssinaturaCanvas from '../../Components/AssinaturaCanvas';
import { baseURL } from '../../../utils/api';
import { CHAVE_TOKEN, real, dataBR, hojeISO, linkWhatsApp } from "../../../utils/formata";
import { Vazio } from "../../Components/Estado";
import CampoCobranca from "../../Components/CampoCobranca";
import { lerFrequencia, rotuloFrequencia, obsComFrequencia } from "../../../utils/cobrancaLote";

const NUMERO_ROBO = "554789163477";
// barra das parcelas marcadas: presa no rodapé da tela (no celular, de ponta a ponta com 16px; no PC, à direita)
const barraLote = {
    position: "fixed", left: 16, right: 16, bottom: 16, maxWidth: 520, marginLeft: "auto", zIndex: 150,
    background: "#132436", border: "1px solid #d4af37", borderRadius: 16, padding: "12px 16px 14px",
    boxShadow: "0 14px 40px rgba(0, 0, 0, 0.55)",
};
const botaoBarraLote = { flex: "1 1 auto", minHeight: 48, fontWeight: 700, whiteSpace: "nowrap" };

const rotulos = {
    analise: "Em análise",
    ativo: "Ativo",
    quitado: "Quitado",
    cancelado: "Cancelado",
};

const tiposDoc = {
    contrato_social: "Contrato Social",
    documento_socio: "Documento do Sócio",
    comprovante_endereco: "Comprovante de Endereço",
    contrato_assinado: "Contrato Assinado",
    comprovante_pagamento: "Comprovante de Pagamento",
    outro: "Outro",
};

export default function EmprestimoDetalhe() {
    const [emp, setEmp] = useState(null);
    const [pagando, setPagando] = useState(null); // parcela em pagamento
    const [pagamento, setPagamento] = useState({});
    const [assinando, setAssinando] = useState(false);
    const [pix, setPix] = useState(null);
    const [enviandoDoc, setEnviandoDoc] = useState(false);
    const [novoDoc, setNovoDoc] = useState({ tipo: "contrato_assinado" });
    const [renovacaoFechada, setRenovacaoFechada] = useState(false);
    // cobrança/baixa em lote (Dani, 19/09): as parcelas marcadas viram UM PIX no WhatsApp ou uma baixa só
    const [marcadas, setMarcadas] = useState([]);
    const [recebendoLote, setRecebendoLote] = useState(null);
    const [cobrandoLote, setCobrandoLote] = useState(false);
    const [avisoLote, setAvisoLote] = useState(null);
    const [frequencia, setFrequencia] = useState(null); // edição da frequência de cobrança do cliente
    const { consultarEmprestimo, pagarParcela, estornarParcela, liberarEmprestimo, cancelarEmprestimo, removerEmprestimo, assinarEmprestimo, adicionarDocumento, removerDocumento, gerarPixParcela, salvarObsEmpresa } = useHelp();
    const { setErroPadrao } = useErroPadrao();
    const navigate = useNavigate();
    const { id } = useParams();

    const carregar = useCallback(() => {
        consultarEmprestimo(id)
            .then((data) => setEmp(data))
            .catch((error) => setErroPadrao(error));
    }, [id, consultarEmprestimo, setErroPadrao]);

    useEffect(() => {
        if (!localStorage.getItem(CHAVE_TOKEN)) {
            navigate("/login");
            return;
        }
        carregar();
    }, [carregar, navigate]);

    if (!emp) {
        return <div style={{padding: "3em", width: "100%", textAlign: "center"}}><span className="spinner-border" style={{color: "#d4af37"}}></span></div>;
    }

    const empresa = emp.Empresa || {};
    const parcelas = [...(emp.Parcelas || [])].sort((a, b) => a.numero - b.numero);
    const pagas = parcelas.filter((p) => p.status === "paga");
    const totalPago = pagas.reduce((s, p) => s + Number(p.valorPago || p.valor), 0);
    const saldoDevedor = parcelas.filter((p) => p.status !== "paga").reduce((s, p) => s + Number(p.valor), 0);
    const progresso = Math.round((pagas.length / (parcelas.length || 1)) * 100);
    const hoje = hojeISO();
    // renovação: empréstimo ativo com 3 ou menos parcelas por pagar (Dani, 18/09). "Agora não" vale pra essa quantidade restante
    const restantes = parcelas.filter((p) => p.status !== "paga");
    const atrasadas = restantes.filter((p) => p.dataVencimento < hoje);
    const chaveRenovacao = `renovacao-vista-${emp.id}-${restantes.length}`;
    const renovacaoJaVista = (() => { try { return localStorage.getItem(chaveRenovacao) === "1"; } catch { return false; } })();
    const mostrarRenovacao = emp.status === "ativo" && restantes.length > 0 && restantes.length <= 3 && !renovacaoFechada && !renovacaoJaVista;
    const fecharRenovacao = () => { try { localStorage.setItem(chaveRenovacao, "1"); } catch {} setRenovacaoFechada(true); };
    const primeiroNome = String(empresa.responsavel || "").trim().split(/\s+/)[0] || "";
    const nomeCliente = /^[A-Za-zÀ-ÿ]{2,}$/.test(primeiroNome) && !/^a$/i.test(primeiroNome) ? primeiroNome.charAt(0).toUpperCase() + primeiroNome.slice(1).toLowerCase() : "";
    const freqCliente = lerFrequencia(empresa.obs);
    const abertasAtivo = emp.status === "ativo" ? restantes.filter((p) => p.status === "pendente") : [];
    const selecionadas = abertasAtivo.filter((p) => marcadas.includes(p.id));
    const totalSelecionado = Math.round(selecionadas.reduce((s, p) => s + Number(p.valor), 0) * 100) / 100;
    const numerosSelecionados = selecionadas.map((p) => p.numero).join(", ");
    const vencidasAteHoje = abertasAtivo.filter((p) => p.dataVencimento <= hoje);
    const marcarLista = (lista) => setMarcadas(lista.map((p) => p.id));
    const alternarMarcada = (idParcela) => setMarcadas((m) => (m.includes(idParcela) ? m.filter((x) => x !== idParcela) : [...m, idParcela]));

    function handlePagar(manterSaldo = false) {
        pagarParcela(pagando.id, { ...pagamento, manterSaldo: manterSaldo === true })
            .then(() => { setPagando(null); setPagamento({}); carregar(); })
            .catch((error) => setErroPadrao(error));
    }

    function handleSubmitPagar(e) {
        e.preventDefault();
        handlePagar(false);
    }

    // Baixa de várias parcelas de uma vez, pelo valor cheio de cada uma, uma depois da outra.
    // O robô junta as que caírem juntas num recibo só pro cliente.
    async function handleReceberLote(e) {
        e.preventDefault();
        const lista = selecionadas;
        const { dataPagamento, formaPagamento } = recebendoLote;
        let feitas = 0;
        setRecebendoLote((r) => ({ ...r, enviando: true, feitas: 0 }));
        try {
            for (const p of lista) {
                await pagarParcela(p.id, { valorPago: p.valor, dataPagamento, formaPagamento, manterSaldo: false });
                const ate = ++feitas;
                setRecebendoLote((r) => ({ ...r, feitas: ate }));
            }
            setMarcadas([]);
            setAvisoLote({ tipo: "ok", msg: `${feitas} ${feitas === 1 ? "parcela recebida" : "parcelas recebidas"} (${real(lista.reduce((s, p) => s + Number(p.valor), 0))}). O robô manda o recibo pro cliente.` });
        } catch (error) {
            setMarcadas(lista.slice(feitas).map((p) => p.id));
            setAvisoLote({ tipo: "erro", msg: `Parou na parcela ${lista[feitas].numero}: ${feitas} de ${lista.length} recebidas. As que faltam continuam marcadas.` });
            setErroPadrao(error);
        } finally {
            setRecebendoLote(null);
            carregar();
        }
    }

    // Cobrança das marcadas num PIX só: o robô (porta local deste PC) manda a lista + o PIX do total.
    // Robô fora deste PC (ou no celular): abre o WhatsApp com o comando "#lote" pronto.
    async function handleCobrarLote() {
        const nums = selecionadas.map((p) => p.numero);
        setCobrandoLote(true);
        setAvisoLote(null);
        const controle = new AbortController();
        const alarme = setTimeout(() => controle.abort(), 90000);
        try {
            const r = await fetch("http://127.0.0.1:3456/cobrar-lote", {
                method: "POST",
                headers: { "X-Help": "1", "Content-Type": "application/json" },
                body: JSON.stringify({ contrato: emp.id, parcelas: nums }),
                signal: controle.signal,
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok || !j.ok) throw new Error(j.mensagem || `o robô respondeu ${r.status}`);
            setMarcadas([]);
            setAvisoLote({ tipo: "ok", msg: j.mensagem });
        } catch (e) {
            if (e.name === "AbortError") {
                // pode ter saído devagar: não reabre pelo WhatsApp pra não cobrar duas vezes
                setAvisoLote({ tipo: "erro", msg: "O robô demorou pra responder. Confira no WhatsApp se a cobrança saiu antes de tentar de novo." });
            } else if (/Failed to fetch|NetworkError|Load failed/i.test(String(e.message))) {
                const texto = encodeURIComponent(`#lote ${emp.id} ${nums.join(",")}`);
                const celular = /Android|iPhone|iPad/i.test(navigator.userAgent);
                window.open(celular ? `https://wa.me/${NUMERO_ROBO}?text=${texto}` : `https://web.whatsapp.com/send?phone=${NUMERO_ROBO}&text=${texto}`, "_blank", "noopener");
                setAvisoLote({ tipo: "manual", msg: "Abri o WhatsApp com o comando pronto. Toque em ENVIAR lá que o robô manda a cobrança com um PIX só." });
            } else {
                setAvisoLote({ tipo: "erro", msg: `Não deu: ${e.message}` });
            }
        } finally {
            clearTimeout(alarme);
            setCobrandoLote(false);
        }
    }

    function handleSalvarFrequencia() {
        salvarObsEmpresa(empresa, obsComFrequencia(empresa.obs, frequencia))
            .then(() => { setFrequencia(null); carregar(); })
            .catch((error) => setErroPadrao(error));
    }

    function handlePix(parcela) {
        gerarPixParcela(parcela.id)
            .then((r) => setPix({ parcela, ...r }))
            .catch((error) => setErroPadrao(error));
    }

    function handleEstornar(idParcela) {
        if (!window.confirm("Estornar esse recebimento? A entrada sai do caixa.")) return;
        estornarParcela(idParcela).then(carregar).catch((error) => setErroPadrao(error));
    }

    function handleLiberar() {
        const aviso =
            `Confirma a VISITA e libera ${real(emp.valorPrincipal)} para ${empresa.razaoSocial}?\n\n` +
            `• Registra a saída no caixa\n` +
            `• A partir daí o cliente passa a receber a cobrança diária com PIX\n\n` +
            `Só confirme se o dinheiro foi mesmo entregue.`;
        if (!window.confirm(aviso)) return;
        liberarEmprestimo(emp.id).then(carregar).catch((error) => setErroPadrao(error));
    }

    function handleCancelar() {
        if (!window.confirm("Cancelar esse empréstimo?")) return;
        cancelarEmprestimo(emp.id).then(carregar).catch((error) => setErroPadrao(error));
    }

    function handleExcluir() {
        if (!window.confirm("EXCLUIR esse empréstimo e todas as parcelas? Essa ação não tem volta.")) return;
        removerEmprestimo(emp.id)
            .then((status) => { if (status === 200) navigate("/emprestimos"); })
            .catch((error) => setErroPadrao(error));
    }

    function handleAssinar(assinatura) {
        assinarEmprestimo(emp.id, assinatura)
            .then(() => { setAssinando(false); carregar(); })
            .catch((error) => setErroPadrao(error));
    }

    function handleEnviarDoc(e) {
        e.preventDefault();
        if (!novoDoc.arquivo) return;
        setEnviandoDoc(true);
        const formData = new FormData();
        formData.append("arquivo", novoDoc.arquivo);
        formData.append("tipo", novoDoc.tipo);
        formData.append("nome", novoDoc.nome || novoDoc.arquivo.name);
        formData.append("EmprestimoId", emp.id);
        formData.append("EmpresaId", empresa.id);
        adicionarDocumento(formData)
            .then(() => { setNovoDoc({ tipo: "contrato_assinado" }); carregar(); })
            .catch((error) => setErroPadrao(error))
            .finally(() => setEnviandoDoc(false));
    }

    function handleExcluirDoc(idDoc) {
        if (!window.confirm("Excluir esse documento?")) return;
        removerDocumento(idDoc).then(carregar).catch((error) => setErroPadrao(error));
    }

    // <a href> não manda header: o token vai na query pra API liberar o arquivo
    const urlArquivos = `${baseURL}/arquivos`;
    const comToken = (arquivo) =>
        `${urlArquivos}/${arquivo}?token=${encodeURIComponent(localStorage.getItem(CHAVE_TOKEN) || "")}`;

    return (
        <div className="pagina">
            <div className="cabecalho-pagina">
                <h2 className="he-titulo">Empréstimo <span className="he-dourado">#{emp.id}</span> <span className={`badge badge-${emp.status}`} style={{fontSize: "clamp(0.7rem, 2.5vw, 0.9rem)", verticalAlign: "middle"}}>{rotulos[emp.status]}</span></h2>
                <div className="acoes-linha rotulos-longos">
                    {emp.status === "analise" && <button className="btn btn-he largura-total" onClick={handleLiberar}><i className="bi bi-geo-alt-fill"></i> Visita OK — liberar dinheiro</button>}
                    <Link className="btn btn-outline-he" to={`/contrato/${emp.id}`}><i className="bi bi-file-earmark-text"></i> Ver contrato</Link>
                    {(emp.status === "ativo" || emp.status === "quitado") &&
                        <Link className="btn btn-he" to={`/renovar-emprestimo/${emp.id}`}><i className="bi bi-arrow-repeat"></i> Renovar contrato</Link>}
                    {emp.status !== "cancelado" && emp.status !== "quitado" &&
                        <Link className="btn btn-outline-he" to={`/editar-emprestimo/${emp.id}`}><i className="bi bi-pencil-square"></i> Editar</Link>}
                    {!emp.assinaturaBase64 && emp.status !== "cancelado" && <button className="btn btn-outline-he" onClick={() => setAssinando(true)}><i className="bi bi-pen"></i> Colher assinatura</button>}
                    {emp.status === "analise" && <button className="btn btn-outline-danger" onClick={handleCancelar}>Cancelar</button>}
                    <button className="btn btn-danger" onClick={handleExcluir}><i className="bi bi-trash"></i><span className="so-mobile-inline"> Excluir</span></button>
                </div>
            </div>

            {/* RENOVAÇÃO (Dani, 18/09): faltando 3 parcelas ou menos, o empréstimo abre com o convite pra renovar */}
            {mostrarRenovacao &&
                <div className="modal-fundo" onClick={(e) => { if (e.target === e.currentTarget) fecharRenovacao(); }}>
                    <div className="modal-caixa" role="dialog" aria-modal="true" aria-label="Hora de oferecer a renovação">
                        <div className="modal-cabecalho">
                            <div>
                                <p className="modal-titulo"><i className="bi bi-arrow-repeat"></i> Hora de oferecer a renovação</p>
                                <p className="modal-subtitulo">{empresa.nomeFantasia || empresa.razaoSocial}{nomeCliente ? ` · ${nomeCliente}` : ""}</p>
                            </div>
                            <button type="button" className="modal-fechar" onClick={fecharRenovacao} aria-label="Fechar">×</button>
                        </div>
                        <div className="modal-valor">
                            <span>Faltam para terminar o contrato</span>
                            <strong style={{whiteSpace: "nowrap"}}>{restantes.length} {restantes.length === 1 ? "parcela" : "parcelas"}</strong>
                        </div>
                        <div className="modal-corpo">
                            <p style={{margin: 0, color: "#e8eef5", lineHeight: 1.5}}>
                                Já pagou <strong>{pagas.length} de {parcelas.length}</strong> ({real(totalPago)}). Faltam <strong>{real(saldoDevedor)}</strong> — a última vence em <strong>{dataBR(restantes[restantes.length - 1].dataVencimento)}</strong>.
                            </p>
                            {atrasadas.length > 0
                                ? <div className="modal-aviso-parcial"><i className="bi bi-exclamation-triangle-fill"></i> {atrasadas.length === 1 ? "1 parcela está atrasada" : `${atrasadas.length} parcelas estão atrasadas`} — confira antes de renovar.</div>
                                : <p style={{margin: 0, color: "#2ecc71", fontWeight: 600}}><i className="bi bi-check-circle-fill"></i> Nenhuma parcela atrasada — bom momento pra renovar.</p>}
                        </div>
                        <div className="modal-acoes" style={{flexDirection: "column"}}>{/* três botões lado a lado quebravam o texto no computador */}
                            <Link className="btn btn-he" to={`/renovar-emprestimo/${emp.id}`} onClick={fecharRenovacao}><i className="bi bi-arrow-repeat"></i> Renovar contrato</Link>
                            {empresa.telefone &&
                                <a className="btn btn-success" target="_blank" rel="noreferrer" onClick={fecharRenovacao}
                                   href={linkWhatsApp(empresa.telefone, `Olá${nomeCliente ? `, ${nomeCliente}` : ""}! Aqui é a Daniele, da Help Empresarial 💛 Faltam só ${restantes.length} ${restantes.length === 1 ? "diária" : "diárias"} pra você concluir o seu contrato — parabéns pelo compromisso! Se quiser, já deixo pronta a renovação do seu crédito. Quer que eu veja um valor pra você?`)}>
                                    <i className="bi bi-whatsapp"></i> Oferecer no WhatsApp
                                </a>}
                            <button type="button" className="btn btn-texto-suave" onClick={fecharRenovacao}>Agora não</button>
                        </div>
                    </div>
                </div>
            }

            {/* Resumo */}
            <div className="he-card" style={{margin: "1em 0"}}>
                <h5 style={{fontWeight: 700}}>{empresa.nomeFantasia || empresa.razaoSocial}</h5>
                <div style={{color: "#9aa5b5", fontSize: "0.85em"}}>{empresa.tipoPessoa === "fisica" ? <><span className="badge badge-analise" style={{fontSize: "0.7em"}}>Pessoa Física</span> CPF {empresa.cnpj || "—"}</> : <>{empresa.razaoSocial} · CNPJ {empresa.cnpj}</>}</div>
                <div style={{marginTop: 8, fontSize: "0.9em", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 14px"}}>
                    <span><i className="bi bi-person he-dourado"></i> {empresa.responsavel}</span>
                    <span><i className="bi bi-telephone he-dourado"></i> {empresa.telefone}</span>
                    {empresa.telefone && <a className="btn btn-sm btn-success" target="_blank" rel="noreferrer" href={linkWhatsApp(empresa.telefone, `Olá ${empresa.responsavel}! Aqui é da Help Empresarial, sobre o contrato #${emp.id}.`)}><i className="bi bi-whatsapp"></i><span className="so-mobile-inline"> WhatsApp</span></a>}
                </div>
                <div style={{marginTop: 14}}>
                    <div style={{display: "flex", justifyContent: "space-between", fontSize: "0.82rem", color: "#a3aec0", gap: 8, fontWeight: 600}}>
                        <span>Progresso: {pagas.length}/{parcelas.length} parcelas</span><span className="he-dourado">{progresso}%</span>
                    </div>
                    <div className="barra-progresso">
                        <div style={{width: `${progresso}%`}}></div>
                    </div>
                </div>
            </div>

            <div className="cards-estatistica" style={{margin: "1em 0"}}>
                <div className="card-estat">
                    <div className="estat-rotulo">EMPRESTADO</div>
                    <div className="estat-numero" style={{color: "#f3f4f6"}}>{real(emp.valorPrincipal)}</div>
                </div>
                <div className="card-estat">
                    <div className="estat-rotulo">TOTAL C/ JUROS ({Number(emp.taxaJuros)}% a.m. {emp.metodoJuros})</div>
                    <div className="estat-numero">{real(emp.valorTotal)}</div>
                </div>
                <div className="card-estat">
                    <div className="estat-rotulo">JÁ RECEBIDO</div>
                    <div className="estat-numero" style={{color: "#2ecc71"}}>{real(totalPago)}</div>
                </div>
                <div className="card-estat">
                    <div className="estat-rotulo">SALDO DEVEDOR</div>
                    <div className="estat-numero" style={{color: saldoDevedor > 0 ? "#ff6b5b" : "#2ecc71"}}>{real(saldoDevedor)}</div>
                </div>
            </div>

            <div style={{display: "flex", gap: "1em", flexWrap: "wrap", margin: "1em 0"}}>
                <div className="he-card" style={{flex: "1 1 260px", minWidth: 0}}>
                    <div className="estat-rotulo">ASSINATURA</div>
                    {emp.assinaturaBase64 ?
                        <>
                            <img src={emp.assinaturaBase64} alt="Assinatura" style={{maxWidth: "100%", background: "#fff", borderRadius: 6, marginTop: 4}}/>
                            <div style={{fontSize: "0.82rem", color: "#2ecc71", fontWeight: 600, marginTop: 6}}><i className="bi bi-check-circle"></i> {emp.nomeSignatario}</div>
                        </>
                        :
                        <div style={{color: "#ffc107", fontSize: "0.9em", marginTop: 4}}><i className="bi bi-exclamation-circle"></i> Contrato ainda não assinado</div>
                    }
                </div>
                <div className="he-card" style={{flex: "1 1 260px", minWidth: 0}}>
                    <div className="estat-rotulo">VISITA AO ESTABELECIMENTO</div>
                    {emp.visitaData ?
                        <>
                            <div style={{fontWeight: 700, color: "#d4af37", marginTop: 4}}>
                                <i className="bi bi-geo-alt-fill"></i> {dataBR(emp.visitaData)} às {emp.visitaHora}
                            </div>
                            <div style={{fontSize: "0.8em", marginTop: 4}}>{emp.visitaEndereco}</div>
                        </>
                        :
                        <div style={{color: "#ffc107", fontSize: "0.9em", marginTop: 4}}><i className="bi bi-exclamation-circle"></i> Visita ainda não agendada</div>
                    }
                </div>
            </div>

            {/* PIX gerado */}
            {pix &&
                <div className="he-card" style={{margin: "1em 0", border: "1px solid #2ecc71"}}>
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8}}>
                        <h5 style={{color: "#2ecc71", fontWeight: 700, margin: 0}}>
                            <i className="bi bi-qr-code"></i> PIX da parcela {pix.parcela.numero} — {real(pix.parcela.valor)}
                        </h5>
                        <button className="btn btn-sm btn-outline-he" onClick={() => setPix(null)}>Fechar</button>
                    </div>
                    <p style={{color: "#9aa5b5", fontSize: "0.85em", margin: "8px 0"}}>
                        Assim que o cliente pagar, a baixa acontece sozinha e ele recebe o recibo. Manda o código abaixo pra ele:
                    </p>
                    <div style={{display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start"}}>
                        {pix.imagemQrCode && <img src={pix.imagemQrCode} alt="QR Code PIX" style={{width: 180, maxWidth: "100%", background: "#fff", padding: 8, borderRadius: 8}}/>}
                        <div style={{flex: "1 1 300px", minWidth: 0}}>
                            <textarea className="form-control codigo-pix" readOnly rows={4} value={pix.copiaECola} onFocus={(e) => e.target.select()}/>
                            <div className="acoes-linha rotulos-longos" style={{marginTop: 8}}>
                                <button className="btn btn-sm btn-he" onClick={() => navigator.clipboard.writeText(pix.copiaECola)}>
                                    <i className="bi bi-clipboard"></i> Copiar código
                                </button>
                                <a className="btn btn-sm btn-success" target="_blank" rel="noreferrer"
                                   href={linkWhatsApp(empresa.telefone, `Olá ${empresa.responsavel}! Segue o PIX da parcela ${pix.parcela.numero} (${real(pix.parcela.valor)}) da Help Empresarial:\n\n${pix.copiaECola}`)}>
                                    <i className="bi bi-whatsapp"></i> Enviar no WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            }

            {/* Modal assinatura */}
            {assinando &&
                <div className="he-card" style={{margin: "1em 0", border: "1px solid #d4af37"}}>
                    <h5 className="he-titulo he-dourado"><i className="bi bi-pen"></i> Assinatura do contrato</h5>
                    <AssinaturaCanvas onConfirmar={handleAssinar} onCancelar={() => setAssinando(false)}/>
                </div>
            }

            <div style={{display: "flex", gap: "1em", flexWrap: "wrap", alignItems: "flex-start"}}>
                {/* Parcelas */}
                <div className={`he-card sem-rolagem ${pagando ? "sem-vidro" : ""}`} style={{flex: "2 1 500px", minWidth: 0}}>
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: "0.75rem"}}>
                        <h5 className="he-titulo he-dourado" style={{margin: 0}}><i className="bi bi-list-ol"></i> Parcelas</h5>
                        <button type="button" className="btn btn-sm btn-outline-he" style={{whiteSpace: "nowrap"}} title="Frequência da cobrança deste cliente" onClick={() => setFrequencia(freqCliente)}>
                            <i className="bi bi-calendar-week"></i> Cobrança: {rotuloFrequencia(freqCliente)}
                        </button>
                    </div>
                    {abertasAtivo.length > 1 &&
                        <div style={{display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10}}>
                            <span style={{color: "#9aa5b5", fontSize: "0.85rem"}}>Marcar:</span>
                            {vencidasAteHoje.length > 0 &&
                                <button type="button" className="btn btn-sm btn-outline-he" style={{whiteSpace: "nowrap"}} onClick={() => marcarLista(vencidasAteHoje)}>Vencidas até hoje ({vencidasAteHoje.length})</button>}
                            <button type="button" className="btn btn-sm btn-outline-he" style={{whiteSpace: "nowrap"}} onClick={() => marcarLista(abertasAtivo.slice(0, 6))}>Próximas {Math.min(6, abertasAtivo.length)}</button>
                            <button type="button" className="btn btn-sm btn-outline-he" style={{whiteSpace: "nowrap"}} onClick={() => marcarLista(abertasAtivo)}>Todas em aberto ({abertasAtivo.length})</button>
                        </div>
                    }
                    {avisoLote &&
                        <div role="status" style={{display: "flex", gap: 8, alignItems: "flex-start", justifyContent: "space-between", padding: "10px 12px", borderRadius: 10, marginBottom: 10, fontSize: "0.9rem", color: "#e8eef5",
                            background: avisoLote.tipo === "erro" ? "rgba(255, 107, 91, 0.12)" : "rgba(46, 204, 113, 0.12)", border: `1px solid ${avisoLote.tipo === "erro" ? "#ff6b5b" : "#2ecc71"}`}}>
                            <span><i className={`bi ${avisoLote.tipo === "erro" ? "bi-exclamation-triangle-fill" : avisoLote.tipo === "manual" ? "bi-whatsapp" : "bi-check-circle-fill"}`} style={{marginRight: 6}}></i>{avisoLote.msg}</span>
                            <button type="button" className="modal-fechar" style={{minWidth: 32, minHeight: 32, fontSize: "1.3rem"}} onClick={() => setAvisoLote(null)} aria-label="Fechar aviso">×</button>
                        </div>
                    }
                    <div className="tabela-rolavel rolagem-so-desktop">
                    <table className="table table-striped table-hover tabela-responsiva tabela-mini">
                        <thead>
                            <tr><th style={{whiteSpace: "nowrap"}}>
                                {abertasAtivo.length > 0 &&
                                    <input type="checkbox" className="form-check-input" style={{margin: "0 8px 0 0", verticalAlign: "middle"}} aria-label="Marcar todas em aberto"
                                        checked={selecionadas.length === abertasAtivo.length} onChange={(e) => marcarLista(e.target.checked ? abertasAtivo : [])}/>}
                                Nº</th><th>Vencimento</th><th>Valor</th><th>Status</th><th>Pagamento</th><th style={{width: 120}}></th></tr>
                        </thead>
                        <tbody>
                            {parcelas.map((p) => {
                                const atrasada = p.status === "pendente" && p.dataVencimento < hoje;
                                return (
                                    <tr key={p.id}>
                                        <td className="principal" data-rotulo="Parcela">
                                            {p.status === "pendente" && emp.status === "ativo" ?
                                                <label style={{display: "inline-flex", alignItems: "center", gap: 8, margin: 0, cursor: "pointer"}}>
                                                    <input type="checkbox" className="form-check-input" style={{width: 20, height: 20, margin: 0}} aria-label={`Marcar parcela ${p.numero}`}
                                                        checked={marcadas.includes(p.id)} onChange={() => alternarMarcada(p.id)}/>
                                                    {p.numero}
                                                </label>
                                                : p.numero}
                                        </td>
                                        <td data-rotulo="Vencimento" style={{color: atrasada ? "#ff6b5b" : undefined}}>{dataBR(p.dataVencimento)}</td>
                                        <td className="destaque" data-rotulo="Valor">{real(p.valor)}</td>
                                        <td data-rotulo="Status"><span className={`badge ${atrasada ? "badge-atrasada" : `badge-${p.status}`}`}>{atrasada ? "Atrasada" : p.status === "paga" ? "Paga" : "Pendente"}</span></td>
                                        <td data-rotulo="Pagamento" style={{fontSize: "0.85em", color: "#9aa5b5"}}>
                                            {p.status === "paga" ? `${dataBR(p.dataPagamento)} · ${real(p.valorPago)} · ${p.formaPagamento}` : "—"}
                                        </td>
                                        <td className="acoes">
                                            {p.status === "pendente" && emp.status === "ativo" &&
                                                <div className="acoes-linha">
                                                    <button className="btn btn-sm btn-he" onClick={() => { setPagando(p); setPagamento({ valorPago: p.valor, dataPagamento: hoje, formaPagamento: "pix" }); }}>
                                                        <i className="bi bi-cash"></i> Receber
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-he" title="Gerar PIX (baixa automática ao pagar)" onClick={() => handlePix(p)}>
                                                        <i className="bi bi-qr-code"></i><span className="so-mobile-inline"> Gerar PIX</span>
                                                    </button>
                                                </div>
                                            }
                                            {p.status === "paga" &&
                                                <div className="acoes-linha">
                                                    <button className="btn btn-sm btn-outline-danger" title="Estornar" onClick={() => handleEstornar(p.id)}>
                                                        <i className="bi bi-arrow-counterclockwise"></i><span className="so-mobile-inline"> Estornar</span>
                                                    </button>
                                                </div>
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>

                    {/* Recebimento vem num pop-up centralizado (antes era um form no
                        rodapé da lista, longe do polegar e cortado no celular) */}
                    {pagando &&
                        <div className="modal-fundo" onClick={(e) => { if (e.target === e.currentTarget) setPagando(null); }}>
                            <form onSubmit={handleSubmitPagar} className="modal-caixa form-mobile">
                                <div className="modal-cabecalho">
                                    <div>
                                        <p className="modal-titulo"><i className="bi bi-cash-coin"></i> Receber parcela {pagando.numero}</p>
                                        <p className="modal-subtitulo">{empresa.nomeFantasia || empresa.razaoSocial}</p>
                                    </div>
                                    <button type="button" className="modal-fechar" onClick={() => setPagando(null)} aria-label="Fechar">×</button>
                                </div>

                                <div className="modal-valor">
                                    <span>Valor da parcela</span>
                                    <strong className="valor-dinheiro">{real(pagando.valor)}</strong>
                                </div>

                                <div className="modal-corpo">
                                    <div className="campo-form">
                                        <label className="form-label">Valor recebido</label>
                                        <input className="form-control" type="number" step="0.01" inputMode="decimal" autoFocus value={pagamento.valorPago || ""} onChange={(e) => setPagamento({...pagamento, valorPago: e.target.value})} required/>
                                    </div>
                                    <div className="campo-form">
                                        <label className="form-label">Data do pagamento</label>
                                        <input className="form-control" type="date" value={pagamento.dataPagamento || ""} onChange={(e) => setPagamento({...pagamento, dataPagamento: e.target.value})} required/>
                                    </div>
                                    <div className="campo-form">
                                        <label className="form-label">Forma de pagamento</label>
                                        <select className="form-select" value={pagamento.formaPagamento} onChange={(e) => setPagamento({...pagamento, formaPagamento: e.target.value})}>
                                            <option value="pix">PIX</option>
                                            <option value="dinheiro">Dinheiro</option>
                                            <option value="transferencia">Transferência</option>
                                            <option value="boleto">Boleto</option>
                                            <option value="outro">Outro</option>
                                        </select>
                                    </div>
                                </div>

                                {(() => {
                                    const recebido = Number(pagamento.valorPago);
                                    const temValor = !isNaN(recebido) && recebido > 0;
                                    const faltou = temValor ? Math.round((pagando.valor - recebido) * 100) / 100 : 0;
                                    if (temValor && faltou > 0.005) {
                                        return (
                                            <>
                                                <div className="modal-aviso-parcial">
                                                    <i className="bi bi-exclamation-triangle-fill"></i> Faltam <strong>{real(faltou)}</strong> pra completar a parcela. O que você quer fazer?
                                                </div>
                                                <div className="modal-acoes">
                                                    <button type="button" className="btn btn-he" onClick={() => handlePagar(true)}>
                                                        <i className="bi bi-bookmark-check"></i> Receber {real(recebido)} e deixar {real(faltou)} em aberto
                                                    </button>
                                                    <button type="button" className="btn btn-outline-he" onClick={() => handlePagar(false)}>
                                                        Quitar assim mesmo (desconto de {real(faltou)})
                                                    </button>
                                                    <button type="button" className="btn btn-texto-suave" onClick={() => setPagando(null)}>Cancelar</button>
                                                </div>
                                            </>
                                        );
                                    }
                                    return (
                                        <div className="modal-acoes">
                                            <button type="submit" className="btn btn-he"><i className="bi bi-check-lg"></i> Confirmar recebimento</button>
                                            <button type="button" className="btn btn-outline-he" onClick={() => setPagando(null)}>Cancelar</button>
                                        </div>
                                    );
                                })()}
                            </form>
                        </div>
                    }
                </div>

                {/* Documentos */}
                <div className="he-card" style={{flex: "1 1 340px", minWidth: 0}}>
                    <h5 className="he-titulo he-dourado"><i className="bi bi-folder2-open"></i> Documentos</h5>

                    <form onSubmit={handleEnviarDoc} className="form-mobile" style={{marginBottom: 12}}>
                        <select className="form-select form-select-sm" style={{marginBottom: 8}} value={novoDoc.tipo} onChange={(e) => setNovoDoc({...novoDoc, tipo: e.target.value})}>
                            {Object.entries(tiposDoc).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <input className="form-control form-control-sm" type="file" style={{marginBottom: 8}} onChange={(e) => setNovoDoc({...novoDoc, arquivo: e.target.files[0]})}/>
                        <button type="submit" className="btn btn-sm btn-he" style={{width: "100%"}} disabled={!novoDoc.arquivo || enviandoDoc}>
                            {enviandoDoc ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-cloud-upload"></i> Enviar documento</>}
                        </button>
                    </form>

                    {(emp.Documentos || []).length === 0 && <Vazio icone="bi bi-folder2-open" titulo="Nenhum documento" texto="Envie o contrato assinado e os documentos do sócio por aqui."/>}
                    {(emp.Documentos || []).map((d) => (
                        <div key={d.id} className="linha-documento">
                            <div>
                                <a href={comToken(d.arquivo)} target="_blank" rel="noreferrer" style={{color: "#e8c95c", fontSize: "0.9em"}}><i className="bi bi-file-earmark"></i> {d.nome}</a>
                                <div style={{fontSize: "0.78rem", color: "#a3aec0", marginTop: 2}}>{tiposDoc[d.tipo] || d.tipo}</div>
                            </div>
                            <button className="btn btn-sm btn-outline-danger" title="Excluir documento" onClick={() => handleExcluirDoc(d.id)}><i className="bi bi-trash"></i></button>
                        </div>
                    ))}
                </div>
            </div>

            {/* LOTE (Dani, 19/09): parcelas marcadas → cobrar num PIX só ou receber todas de uma vez */}
            {selecionadas.length > 0 && !recebendoLote &&
                <>
                    <div style={{height: 150}}></div>
                    <div role="region" aria-label="Parcelas marcadas" style={barraLote}>
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8}}>
                            <div style={{minWidth: 0}}>
                                <div style={{color: "#9aa5b5", fontSize: "0.8rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    {selecionadas.length} {selecionadas.length === 1 ? "parcela marcada" : "parcelas marcadas"} · nº {numerosSelecionados}
                                </div>
                                <div style={{color: "#d4af37", fontWeight: 800, fontSize: "1.3rem", lineHeight: 1.2}}>{real(totalSelecionado)}</div>
                            </div>
                            <button type="button" className="modal-fechar" onClick={() => setMarcadas([])} aria-label="Desmarcar todas">×</button>
                        </div>
                        <div style={{display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap"}}>
                            <button type="button" className="btn btn-success" style={botaoBarraLote} disabled={cobrandoLote} onClick={handleCobrarLote}>
                                {cobrandoLote ? <><span className="spinner-border spinner-border-sm"></span> Enviando…</> : <><i className="bi bi-whatsapp"></i> Cobrar num PIX só</>}
                            </button>
                            <button type="button" className="btn btn-he" style={botaoBarraLote} disabled={cobrandoLote} onClick={() => setRecebendoLote({ dataPagamento: hoje, formaPagamento: "pix" })}>
                                <i className="bi bi-cash-coin"></i> Receber
                            </button>
                        </div>
                    </div>
                </>
            }

            {recebendoLote &&
                <div className="modal-fundo" onClick={(e) => { if (e.target === e.currentTarget && !recebendoLote.enviando) setRecebendoLote(null); }}>
                    <form onSubmit={handleReceberLote} className="modal-caixa form-mobile">
                        <div className="modal-cabecalho">
                            <div>
                                <p className="modal-titulo"><i className="bi bi-cash-coin"></i> Receber {selecionadas.length} {selecionadas.length === 1 ? "parcela" : "parcelas"}</p>
                                <p className="modal-subtitulo">{empresa.nomeFantasia || empresa.razaoSocial} · nº {numerosSelecionados}</p>
                            </div>
                            <button type="button" className="modal-fechar" disabled={recebendoLote.enviando} onClick={() => setRecebendoLote(null)} aria-label="Fechar">×</button>
                        </div>
                        <div className="modal-valor">
                            <span>Total recebido</span>
                            <strong className="valor-dinheiro">{real(totalSelecionado)}</strong>
                        </div>
                        <div className="modal-corpo">
                            <div className="campo-form">
                                <label className="form-label">Data do pagamento</label>
                                <input className="form-control" type="date" value={recebendoLote.dataPagamento} disabled={recebendoLote.enviando} onChange={(e) => setRecebendoLote({...recebendoLote, dataPagamento: e.target.value})} required/>
                            </div>
                            <div className="campo-form">
                                <label className="form-label">Forma de pagamento</label>
                                <select className="form-select" value={recebendoLote.formaPagamento} disabled={recebendoLote.enviando} onChange={(e) => setRecebendoLote({...recebendoLote, formaPagamento: e.target.value})}>
                                    <option value="pix">PIX</option>
                                    <option value="dinheiro">Dinheiro</option>
                                    <option value="transferencia">Transferência</option>
                                    <option value="boleto">Boleto</option>
                                    <option value="outro">Outro</option>
                                </select>
                            </div>
                            <small style={{color: "#9aa5b5", lineHeight: 1.45}}>
                                Cada parcela recebe baixa pelo valor cheio. Se o cliente pagou menos em alguma, receba essa pelo botão da própria parcela.
                            </small>
                        </div>
                        <div className="modal-acoes">
                            <button type="submit" className="btn btn-he" style={{whiteSpace: "nowrap"}} disabled={recebendoLote.enviando}>
                                {recebendoLote.enviando
                                    ? <><span className="spinner-border spinner-border-sm"></span> Recebendo {Math.min((recebendoLote.feitas || 0) + 1, selecionadas.length)} de {selecionadas.length}…</>
                                    : <><i className="bi bi-check-lg"></i> Confirmar</>}
                            </button>
                            <button type="button" className="btn btn-outline-he" disabled={recebendoLote.enviando} onClick={() => setRecebendoLote(null)}>Cancelar</button>
                        </div>
                    </form>
                </div>
            }

            {frequencia &&
                <div className="modal-fundo" onClick={(e) => { if (e.target === e.currentTarget) setFrequencia(null); }}>
                    <div className="modal-caixa" role="dialog" aria-modal="true" aria-label="Cobrança do cliente">
                        <div className="modal-cabecalho">
                            <div>
                                <p className="modal-titulo"><i className="bi bi-calendar-week"></i> Cobrança do cliente</p>
                                <p className="modal-subtitulo">{empresa.nomeFantasia || empresa.razaoSocial} · vale pra todos os contratos dele</p>
                            </div>
                            <button type="button" className="modal-fechar" onClick={() => setFrequencia(null)} aria-label="Fechar">×</button>
                        </div>
                        <CampoCobranca valor={frequencia} onChange={setFrequencia}/>
                        <div className="modal-acoes">
                            <button type="button" className="btn btn-he" onClick={handleSalvarFrequencia}><i className="bi bi-check-lg"></i> Salvar</button>
                            <button type="button" className="btn btn-outline-he" onClick={() => setFrequencia(null)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}
